import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, FileText, Eye, X, ChevronRight, ChevronLeft as ChevronLeftIcon, ZoomIn, ZoomOut, Loader2, Download } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageHeaderV2 from '@/components/website/PageHeaderV2';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker using local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const DocumentsPage = () => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // PDF-specific state
  const [numPages, setNumPages] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);

  // Handle PDF document load success
  const onDocumentLoadSuccess = useCallback(({ numPages: totalPages }) => {
    setNumPages(totalPages);
    setPdfLoading(false);
    setPdfError(null);
  }, []);

  // Handle PDF document load error
  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setPdfError('Impossible de charger le document PDF.');
    setPdfLoading(false);
  }, []);

  // Get container width for responsive PDF rendering
  useEffect(() => {
    const updateWidth = () => {
      const container = document.getElementById('pdf-container');
      if (container) {
        setContainerWidth(container.clientWidth - 32); // Account for padding
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [selectedDocument]);

  const documents = useMemo(() => [
    {
      id: 1,
      title: "Théorie et Exercices Respiratoires",
      description: "Guide complet sur les principes théoriques et pratiques de la rééducation respiratoire",
      pages: 24,
      type: "Guide pratique",
      color: "from-teal-500 to-emerald-500",
      images: Array.from({ length: 24 }, (_, i) => `/documents/Théorie et exercices respiratoires (2)-${i + 1}.png`)
    },
    {
      id: 2,
      title: "Récapitulatif COVID Long Complet",
      description: "Document complet sur la prise en charge du COVID long et la rééducation respiratoire associée",
      type: "Document PDF",
      color: "from-sky-500 to-sky-600",
      pdfUrl: "/documents/Récapitulatif_COVID_Long_Complet.pdf"
    },
    {
      id: 3,
      title: "La Cohérence Cardiaque par l'Institut HeartMath",
      description: "Exploration scientifique de la connexion cœur-cerveau, la variabilité de la fréquence cardiaque (VFC) et la cohérence psychophysiologique. Traduction française d'un document mis à disposition par HeartMath LLC. www.heartmath.com",
      type: "Document PDF",
      color: "from-sky-500 to-sky-600",
      pdfUrl: "/documents/La_Science_de_HeartMath.pdf"
    },
    {
      id: 4,
      title: "Cohérence Cardiaque - Guide Pratique",
      description: "Guide pratique sur la cohérence cardiaque : définition, bienfaits, techniques de pratique et applications au quotidien.",
      type: "Document PDF",
      color: "from-sky-400 to-sky-500",
      pdfUrl: "/documents/Coherence-cardiaque.pdf"
    },
    {
      id: 5,
      title: "Les Trente Fonctions du Nez",
      description: "Présentation détaillée des 30 fonctions essentielles du nez et de la respiration nasale pour la santé globale.",
      type: "Présentation PowerPoint",
      color: "from-sky-400 to-sky-500",
      pdfUrl: "/documents/Les_Trente_Fonctions_du_Nez.pptx"
    },
    {
      id: 6,
      title: "Le rôle du CO2 dans la respiration",
      description: "Présentation sur le rôle du CO2 dans la respiration et les mécanismes respiratoires.",
      type: "Document PDF",
      color: "from-cyan-500 to-teal-500",
      pdfUrl: "/documents/Le_role_du_CO2_dans_la_respiration.pdf"
    },
    {
      id: 7,
      title: "Guide de renforcement des muscles respiratoires",
      description: "Programme complet d'entraînement pour renforcer le diaphragme et les muscles respiratoires. Exercices progressifs et conseils pratiques.",
      type: "Document PDF",
      color: "from-sky-500 to-sky-600",
      pdfUrl: "/documents/renforcement_muscles_respiratoires.pdf"
    },
    {
      id: 8,
      title: "La Méthode Wim Hof — Explication scientifique",
      description: "Analyse scientifique de la méthode Wim Hof : hyperventilation contrôlée, rétentions respiratoires, effets sur le système nerveux autonome, la biochimie du sang et l'immunité. Article de blog synthétique.",
      type: "Document PDF",
      color: "from-sky-500 to-cyan-600",
      pdfUrl: "https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/tkuekiaq_Comment%20expliquer%20la%20Me%CC%81thode%20Wim%20Hof%20d%27un%20point%20de%20vue%20scientifique_%20_%20Blog.pdf"
    }
  ], []);

  // Auto-open document if URL parameter is present
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (openParam === 'theorie') {
      const theorieDoc = documents.find(doc => doc.id === 1);
      if (theorieDoc && !selectedDocument) {
        // Use setTimeout to avoid setState during render
        setTimeout(() => {
          setSelectedDocument(theorieDoc);
          setCurrentPage(1);
        }, 0);
      }
    } else if (openParam === 'oa-manual') {
      const oaDoc = documents.find(doc => doc.id === 0);
      if (oaDoc && !selectedDocument) {
        setTimeout(() => {
          setSelectedDocument(oaDoc);
        }, 0);
      }
    }
  }, [searchParams, documents, selectedDocument]);

  // Block keyboard shortcuts for printing, saving, and screenshots when document is open
  useEffect(() => {
    if (!selectedDocument) return;

    const handleKeyDown = (e) => {
      // Block Ctrl+P (Print), Ctrl+S (Save), Ctrl+Shift+S, PrintScreen
      if (
        (e.ctrlKey && (e.key === 'p' || e.key === 'P' || e.key === 's' || e.key === 'S')) ||
        (e.metaKey && (e.key === 'p' || e.key === 'P' || e.key === 's' || e.key === 'S')) ||
        e.key === 'PrintScreen'
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Block beforeprint event
    const handleBeforePrint = (e) => {
      e.preventDefault();
      alert('L\'impression de ce document n\'est pas autorisée.');
      return false;
    };

    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('beforeprint', handleBeforePrint);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, [selectedDocument]);

  const handleViewDocument = (doc) => {
    // Always open in modal viewer
    setSelectedDocument(doc);
    setCurrentPage(1);
    setZoomLevel(1);
    // Reset PDF state
    setNumPages(null);
    setPdfLoading(true);
    setPdfError(null);
  };

  const handleCloseViewer = () => {
    setSelectedDocument(null);
    setCurrentPage(1);
    setZoomLevel(1);
    setNumPages(null);
    setPdfLoading(true);
    setPdfError(null);
    // Remove the 'open' parameter from URL to prevent auto-reopening
    if (searchParams.get('open')) {
      navigate('/documents', { replace: true });
    }
  };

  const handleNextPage = () => {
    const totalPages = selectedDocument.pages || numPages;
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setZoomLevel(1); // Reset zoom when changing page
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setZoomLevel(1); // Reset zoom when changing page
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3)); // Max zoom 3x
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5)); // Min zoom 0.5x
  };

  // Download function that works reliably
  const handleDownload = async (doc) => {
    try {
      const response = await fetch(doc.pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Extract filename from URL
      const filename = doc.pdfUrl.split('/').pop();
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open in new tab
      window.open(doc.pdfUrl, '_blank');
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F0F7FC 55%, #D6EBF6 100%)',
        fontFamily: "Figtree, sans-serif",
      }}
    >
      <SiteHeaderV2 />
      
      {/* Page Header Image */}
      <PageHeaderV2 
        title="Documents et Ressources" 
        subtitle="Documents de référence sur la rééducation respiratoire"
      />
      
      {/* Hero */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xl text-slate-600 leading-relaxed">
            Consultez nos documents de référence sur la rééducation respiratoire.
          </p>
        </div>
      </section>

      {/* Documents List */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {documents.map((doc) => (
              <Card key={doc.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${doc.color} flex items-center justify-center mb-6`}>
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-2xl font-medium mb-3">
                    {doc.title}
                  </h3>
                  
                  <p className="text-slate-600 mb-4">
                    {doc.description}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
                    <span className="px-3 py-1 bg-slate-100 rounded-full">{doc.type}</span>
                    {doc.pages && <span>{doc.pages} pages</span>}
                    {doc.protected && <span className="px-3 py-1 bg-sky-100 text-slate-700 rounded-full">Consultation uniquement</span>}
                  </div>
                  
                  <div className="flex gap-3">
                    {/* Documents with images (like Théorie et Exercices) use the viewer */}
                    {doc.images ? (
                      <Button 
                        onClick={() => handleViewDocument(doc)}
                        className="w-full bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg"
                      >
                        <Eye size={18} className="mr-2" />
                        Consulter
                      </Button>
                    ) : doc.protected ? (
                      /* Protected PDF documents - view only, no download */
                      <Button 
                        onClick={() => handleViewDocument(doc)}
                        className="w-full bg-sky-500 hover:bg-emerald-600 text-white rounded-lg"
                      >
                        <Eye size={18} className="mr-2" />
                        Consulter
                      </Button>
                    ) : (
                      /* PDF documents are downloadable */
                      <Button 
                        onClick={() => handleDownload(doc)}
                        className="w-full bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg"
                      >
                        <Download size={18} className="mr-2" />
                        Télécharger
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Placeholder for future documents */}
          <div className="mt-8 p-8 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl text-center">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">
              D&apos;autres documents seront ajoutés prochainement
            </p>
          </div>
        </div>
      </section>

      {/* Document Viewer Modal - Full screen with improved scroll and protection */}
      {selectedDocument && (
        <div 
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center"
          onContextMenu={(e) => e.preventDefault()}
        >
          <div 
            className="w-full h-full flex flex-col document-protected"
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* CSS Protection Styles */}
            <style>{`
              .document-protected {
                -webkit-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
                -webkit-touch-callout: none;
              }
              .document-protected img,
              .document-protected iframe {
                pointer-events: none;
                -webkit-user-drag: none;
                -khtml-user-drag: none;
                -moz-user-drag: none;
                -o-user-drag: none;
                user-drag: none;
              }
              .document-protected::selection {
                background: transparent;
              }
              .document-protected::-moz-selection {
                background: transparent;
              }
              /* Overlay protection layer */
              .document-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 10;
                background: transparent;
              }
              /* Print protection */
              @media print {
                .document-protected {
                  display: none !important;
                }
                body::after {
                  content: "L'impression de ce document n'est pas autorisée.";
                  display: block;
                  font-size: 24px;
                  text-align: center;
                  padding: 100px;
                }
              }
            `}</style>
            
            {/* Header - Fixed */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-white border-b border-slate-200 shrink-0">
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-xl font-medium truncate">{selectedDocument.title}</h3>
                {(selectedDocument.pages || numPages) && (
                  <p className="text-xs sm:text-sm text-slate-500">
                    Page {currentPage} sur {selectedDocument.pages || numPages}
                  </p>
                )}
                {selectedDocument.pdfUrl && !selectedDocument.pages && !numPages && (
                  <p className="text-xs sm:text-sm text-slate-500">{selectedDocument.type}</p>
                )}
              </div>
              <div className="flex items-center gap-1 sm:gap-2 ml-2">
                {/* Zoom Controls - For both image-based and PDF documents */}
                {(selectedDocument.images || (selectedDocument.pdfUrl && selectedDocument.pdfUrl.endsWith('.pdf'))) && (
                  <div className="flex items-center gap-1 mr-2 sm:mr-4 bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.5}
                      className="p-1.5 sm:p-2 hover:rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Zoom arrière"
                    >
                      <ZoomOut size={16} className="text-slate-600 sm:w-[18px] sm:h-[18px]" />
                    </button>
                    <button
                      onClick={() => setZoomLevel(1)}
                      className="px-1.5 sm:px-2 py-1 text-xs sm:text-sm font-medium text-slate-700 hover:rounded transition-colors min-w-[45px] sm:min-w-[60px] text-center"
                      title="Cliquez pour réinitialiser"
                    >
                      {Math.round(zoomLevel * 100)}%
                    </button>
                    <button
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 3}
                      className="p-1.5 sm:p-2 hover:rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Zoom avant"
                    >
                      <ZoomIn size={16} className="text-slate-600 sm:w-[18px] sm:h-[18px]" />
                    </button>
                  </div>
                )}
                {/* Preset zoom levels - Hidden on mobile */}
                {selectedDocument.images && (
                  <div className="hidden lg:flex items-center gap-1 mr-4">
                    {[1, 1.5, 1.75, 2].map((level) => (
                      <button
                        key={level}
                        onClick={() => setZoomLevel(level)}
                        className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                          zoomLevel === level
                            ? 'bg-cyan-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {Math.round(level * 100)}%
                      </button>
                    ))}
                  </div>
                )}
                <button 
                  onClick={handleCloseViewer}
                  className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Fermer"
                >
                  <X size={20} className="text-slate-600 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            {/* Document Content - Scrollable area with custom scrollbar and protection */}
            <div 
              className="flex-1 overflow-auto bg-slate-100 document-scroll-container"
              style={{
                scrollbarWidth: 'auto',
                scrollbarColor: '#0d9488 #e2e8f0'
              }}
              onContextMenu={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            >
              <style>{`
                .document-scroll-container::-webkit-scrollbar {
                  width: 14px;
                  height: 14px;
                }
                .document-scroll-container::-webkit-scrollbar-track {
                  background: #e2e8f0;
                  border-radius: 7px;
                }
                .document-scroll-container::-webkit-scrollbar-thumb {
                  background: linear-gradient(180deg, #0d9488 0%, #14b8a6 100%);
                  border-radius: 7px;
                  border: 2px solid #e2e8f0;
                }
                .document-scroll-container::-webkit-scrollbar-thumb:hover {
                  background: linear-gradient(180deg, #0f766e 0%, #0d9488 100%);
                }
                .document-scroll-container::-webkit-scrollbar-corner {
                  background: #e2e8f0;
                }
              `}</style>
              {selectedDocument.images ? (
                <div 
                  className="min-h-full p-6 flex items-start justify-center relative"
                  style={{ minWidth: zoomLevel > 1 ? `${100 * zoomLevel}%` : '100%' }}
                >
                  <img 
                    src={selectedDocument.images[currentPage - 1]} 
                    alt={`Page ${currentPage}`}
                    className="shadow-2xl transition-transform duration-200"
                    style={{ 
                      transform: `scale(${zoomLevel})`,
                      transformOrigin: 'top center',
                      maxWidth: zoomLevel <= 1 ? '100%' : 'none',
                      width: zoomLevel <= 1 ? 'auto' : `${100 / zoomLevel}%`,
                      pointerEvents: 'none'
                    }}
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                  {/* Transparent overlay to prevent interaction */}
                  <div className="document-overlay" onContextMenu={(e) => e.preventDefault()} />
                </div>
              ) : selectedDocument.pdfUrl ? (
                <div 
                  id="pdf-container"
                  className="w-full h-full p-2 sm:p-4 flex flex-col items-center justify-start relative overflow-auto"
                >
                  {selectedDocument.pdfUrl.endsWith('.pdf') ? (
                    <div className="w-full flex flex-col items-center">
                      {/* PDF Loading State */}
                      {pdfLoading && (
                        <div className="flex flex-col items-center justify-center py-20">
                          <Loader2 className="w-12 h-12 text-sky-500 animate-spin mb-4" />
                          <p className="text-slate-600">Chargement du document...</p>
                        </div>
                      )}
                      
                      {/* PDF Error State */}
                      {pdfError && (
                        <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                          <FileText size={64} className="text-sky-400 mb-4" />
                          <p className="text-sky-600 font-medium mb-2">{pdfError}</p>
                          <p className="text-slate-500 text-sm">Veuillez réessayer ou contacter l&apos;administrateur.</p>
                        </div>
                      )}
                      
                      {/* PDF Document */}
                      <Document
                        file={selectedDocument.pdfUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={null}
                        className="flex justify-center"
                      >
                        <Page
                          pageNumber={currentPage}
                          width={containerWidth ? Math.min(containerWidth * zoomLevel, 1200) : undefined}
                          className="shadow-2xl"
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          loading={
                            <div className="flex items-center justify-center py-20">
                              <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                            </div>
                          }
                        />
                      </Document>
                      
                      {/* Page indicator for mobile */}
                      {numPages && !pdfLoading && (
                        <p className="text-center text-white/80 text-sm mt-4 sm:hidden">
                          Utilisez les boutons ci-dessous pour naviguer
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center p-8 rounded-xl shadow-lg max-w-lg bg-white">
                      <FileText size={64} className="mx-auto mb-4 text-sky-500" />
                      <h3 className="text-xl font-medium mb-2">{selectedDocument.title}</h3>
                      <p className="text-slate-600 mb-4">{selectedDocument.description}</p>
                      <p className="text-sm text-slate-500 mb-6">
                        Ce document ({selectedDocument.type}) ne peut pas être visualisé directement dans le navigateur.
                      </p>
                      <p className="text-xs text-slate-400">
                        Pour consulter ce document, veuillez contacter l&apos;administrateur.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Navigation - Fixed at bottom - For image-based and PDF documents */}
            {(selectedDocument.images || (selectedDocument.pdfUrl && selectedDocument.pdfUrl.endsWith('.pdf') && numPages)) && (
              <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 pb-16 sm:pb-4 bg-white border-t border-slate-200 shrink-0">
                <Button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  variant="outline"
                  className="rounded-lg text-sm sm:text-base px-2 sm:px-4"
                  data-testid="pdf-prev-page-btn"
                >
                  <ChevronLeftIcon size={16} className="mr-1 sm:mr-2 sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden sm:inline">Précédent</span>
                  <span className="sm:hidden">Préc.</span>
                </Button>

                <div className="flex items-center gap-1 sm:gap-2">
                  {/* Mobile: Show compact page indicator */}
                  <div className="sm:hidden flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center disabled:opacity-40"
                    >
                      -
                    </button>
                    <span className="text-sm font-medium text-white min-w-[60px] text-center">
                      {currentPage} / {selectedDocument.pages || numPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(selectedDocument.pages || numPages, currentPage + 1))}
                      disabled={currentPage === (selectedDocument.pages || numPages)}
                      className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center disabled:opacity-40"
                    >
                      +
                    </button>
                  </div>
                  
                  {/* Desktop: Show page buttons */}
                  <div className="hidden sm:flex items-center gap-2">
                    {Array.from({ length: Math.min(7, selectedDocument.pages || numPages || 1) }, (_, i) => {
                      const totalPages = selectedDocument.pages || numPages || 1;
                      let pageNum;
                      if (totalPages <= 7) {
                        pageNum = i + 1;
                      } else if (currentPage <= 4) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 3) {
                        pageNum = totalPages - 6 + i;
                      } else {
                        pageNum = currentPage - 3 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => { setCurrentPage(pageNum); setZoomLevel(1); }}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-cyan-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`pdf-page-${pageNum}-btn`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Button
                  onClick={handleNextPage}
                  disabled={currentPage === (selectedDocument.pages || numPages)}
                  variant="outline"
                  className="rounded-lg text-sm sm:text-base px-2 sm:px-4"
                  data-testid="pdf-next-page-btn"
                >
                  <span className="hidden sm:inline">Suivant</span>
                  <span className="sm:hidden">Suiv.</span>
                  <ChevronRight size={16} className="ml-1 sm:ml-2 sm:w-[18px] sm:h-[18px]" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <SiteFooterV2 />
    </div>
  );
};

export default DocumentsPage;
